import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useLanguage } from '@/contexts/LanguageContext'
import { ArrowRight, Play } from 'lucide-react'
import { precacheMedia } from '@/utils/cache'

const HomeScreen: React.FC = () => {
  const { t } = useLanguage()
  const [currentMedia, setCurrentMedia] = useState(0)
  const [isVideoPlaying, setIsVideoPlaying] = useState(true)
  const videoRef = useRef<HTMLVideoElement>(null)

  const mediaItems = [
    { type: 'video', src: '/turbine_robot_video_main.mov', duration: 0 }, // Duration will be from video
    { type: 'image', src: '/robot_main_photo.jpeg', duration: 5000 },
    { type: 'image', src: '/robot_operator_1.jpeg', duration: 5000 },
    { type: 'image', src: '/control_room_2.jpeg', duration: 5000 },
    { type: 'image', src: '/turbine_inside_1.jpeg', duration: 5000 }
  ]

  // Preload all hero images and blog card images for instant display
  useEffect(() => {
    const heroImages = mediaItems
      .filter(item => item.type === 'image')
      .map(item => item.src)
    const blogImages = [
      '/field_robot_above_clouds.jpeg',
      '/field_robot_nacelle_1.jpeg',
      '/field_robot_nacelle_2.jpeg',
      '/field_operator_nacelle_1.jpeg',
      '/robot_inside_blade_main.jpeg',
      '/turbines_1.jpeg',
      '/world_biggest_turbine_1.jpg',
      '/world_biggest_turbine_2.jpg',
      '/world_biggest_turbine_3.webp',
      '/world_biggest_turbine_4.png',
      '/rope_access_turbine_1.jpg',
      '/rope_access_turbine_2.webp',
      '/rope_access_turbine_3.jpg',
      '/rope_access_turbine_4.jpg',
      '/offshore_1.jpg',
      '/offshore_2.jpg',
      '/offshore_3.jpg',
      '/offshore_4.webp',
      '/offshore_5.webp',
      '/offshore_6.jpg'
    ]
    // Preload via browser Image objects for immediate rendering
    ;[...heroImages, ...blogImages].forEach(src => {
      const img = new Image()
      img.src = src
    })
    // Also ensure SW cache has them
    precacheMedia([...heroImages, ...blogImages, '/turbine_robot_video_main.mov'])
  }, [])

  const technologies = [
    t.home.technologies.ai,
    t.home.technologies.machineLearning,
    t.home.technologies.iotSensors,
    t.home.technologies.cloudComputing,
    t.home.technologies.view360,
    t.home.technologies.predictiveAnalytics,
    t.home.technologies.computerVision,
    t.home.technologies.edgeDetection,
    t.home.technologies.damageDetection,
    t.home.technologies.anomalyDetection
  ]

  useEffect(() => {
    const currentItem = mediaItems[currentMedia]
    
    if (currentItem.type === 'video') {
      setIsVideoPlaying(true)
      return
    }

    // For images, set timer to switch to next media
    const timer = setTimeout(() => {
      setCurrentMedia((prev) => (prev + 1) % mediaItems.length)
    }, currentItem.duration)

    return () => clearTimeout(timer)
  }, [currentMedia])

  const handleVideoEnd = () => {
    setIsVideoPlaying(false)
    setCurrentMedia((prev) => (prev + 1) % mediaItems.length)
  }


  return (
    <div className="min-h-screen">
      {/* Hero Section with Video/Image Slider */}
      <section className="relative h-screen overflow-hidden">
        {/* Media Background */}
        <div className="absolute inset-0">
          <AnimatePresence mode="wait">
            {mediaItems[currentMedia].type === 'video' ? (
              <motion.video
                key="video"
                ref={videoRef}
                src={mediaItems[currentMedia].src}
                autoPlay
                muted
                playsInline
                onEnded={handleVideoEnd}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1 }}
                className="w-full h-full object-cover"
              />
            ) : (
              <motion.img
                key={mediaItems[currentMedia].src}
                src={mediaItems[currentMedia].src}
                alt={t.ui.turbine}
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1 }}
                className="w-full h-full object-cover"
              />
            )}
          </AnimatePresence>
        </div>

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/40" />

        {/* Content Overlay */}
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
            >
              <h1 className="text-3xl md:text-5xl lg:text-7xl font-bold text-white leading-tight mb-4 md:mb-6 drop-shadow-2xl">
                {t.home.heroTitle}
                <span className="block text-primary-300 mt-1 md:mt-2">{t.home.heroSubtitle}</span>
              </h1>
              <p className="text-base md:text-xl lg:text-2xl text-white/90 mb-6 md:mb-8 leading-relaxed drop-shadow-lg max-w-3xl">
                {t.home.heroDescription}
              </p>
            </motion.div>
          </div>
        </div>

        {/* Media Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex gap-2">
          {mediaItems.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentMedia(index)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                index === currentMedia 
                  ? 'w-8 bg-white' 
                  : 'w-1.5 bg-white/50 hover:bg-white/70'
              }`}
              aria-label={`Go to media ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-8 md:py-12 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-6 md:mb-10 lg:mb-14">
            <span className="inline-block px-3 md:px-4 py-1 md:py-1.5 bg-primary-100 rounded-full text-primary-700 text-xs md:text-sm font-semibold mb-2 md:mb-4">
              Blog
            </span>
            <h2 className="text-xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 md:mb-4">
              {t.blog.sectionTitle}
            </h2>
            <p className="text-sm md:text-base lg:text-lg text-gray-600 max-w-2xl mx-auto">
              {t.blog.sectionDescription}
            </p>
          </div>

          {/* Grid Layout */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Blog Card 0 - Guncel Saha Calismasi */}
            <div className="group">
              <Link to="/blog/field-work-nacelle-robot" className="block h-full">
                <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 bg-white border border-gray-100 h-full flex flex-col">
                  <div className="relative h-48 md:h-56 overflow-hidden">
                    <img
                      src="/field_robot_above_clouds.jpeg"
                      alt={t.blog.posts.fieldWorkNacelleRobot.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute top-4 right-4 px-3 py-1 bg-emerald-600 text-white text-xs font-semibold rounded-full">
                      {t.blog.categoryFieldWork}
                    </div>
                  </div>
                  <div className="p-5 md:p-6 flex flex-col flex-1">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {t.blog.posts.fieldWorkNacelleRobot.title}
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 mb-4 line-clamp-3 flex-1">
                      {t.blog.posts.fieldWorkNacelleRobot.subtitle}
                    </p>
                    <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-xs leading-5 text-gray-500">{t.blog.posts.fieldWorkNacelleRobot.date} • {t.blog.posts.fieldWorkNacelleRobot.readTime}</span>
                      <span className="inline-flex items-center gap-1 text-primary-600 font-medium text-sm">
                        {t.blog.readMore}
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            {/* Blog Card 1 - LiDAR Robot */}
            <div className="group">
              <Link to="/blog/robot-lidar-inspection" className="block h-full">
                <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 bg-white border border-gray-100 h-full flex flex-col">
                  <div className="relative h-48 md:h-56 overflow-hidden">
                    <video
                      src="/turbine_robot_video_main.mov"
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full">
                      <Play className="w-4 h-4 text-white fill-white" />
                      <span className="text-white text-xs font-medium">{t.blog.videoLabel}</span>
                    </div>
                    <div className="absolute top-4 right-4 px-3 py-1 bg-primary-500 text-white text-xs font-semibold rounded-full">
                      {t.blog.categoryTechnology}
                    </div>
                  </div>
                  <div className="p-5 md:p-6 flex flex-col flex-1">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {t.blog.posts.robotLidarInspection.title}
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 mb-4 line-clamp-3 flex-1">
                      {t.blog.posts.robotLidarInspection.subtitle}
                    </p>
                    <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-xs leading-5 text-gray-500">{t.blog.posts.robotLidarInspection.date} • {t.blog.posts.robotLidarInspection.readTime}</span>
                      <span className="inline-flex items-center gap-1 text-primary-600 font-medium text-sm">
                        {t.blog.readMore}
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            {/* Blog Card 2 - Drone, Robot, Operator */}
            <div className="group">
              <Link to="/blog/drone-robot-inspection" className="block h-full">
                <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 bg-white border border-gray-100 h-full flex flex-col">
                  <div className="relative h-48 md:h-56 overflow-hidden">
                    <img
                      src="/robot_inside_blade_main.jpeg"
                      alt={t.blog.posts.droneRobotInspection.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute top-4 right-4 px-3 py-1 bg-secondary-600 text-white text-xs font-semibold rounded-full">
                      {t.blog.categoryInspection}
                    </div>
                  </div>
                  <div className="p-5 md:p-6 flex flex-col flex-1">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {t.blog.posts.droneRobotInspection.title}
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 mb-4 line-clamp-3 flex-1">
                      {t.blog.posts.droneRobotInspection.subtitle}
                    </p>
                    <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-xs leading-5 text-gray-500">{t.blog.posts.droneRobotInspection.date} • {t.blog.posts.droneRobotInspection.readTime}</span>
                      <span className="inline-flex items-center gap-1 text-primary-600 font-medium text-sm">
                        {t.blog.readMore}
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            {/* Blog Card 3 - Wind Turbine Technology */}
            <div className="group">
              <Link to="/blog/wind-turbine-technology" className="block h-full">
                <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 bg-white border border-gray-100 h-full flex flex-col">
                  <div className="relative h-48 md:h-56 overflow-hidden">
                    <img
                      src="/turbines_1.jpeg"
                      alt={t.blog.posts.windTurbineTechnology.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute top-4 right-4 px-3 py-1 bg-green-600 text-white text-xs font-semibold rounded-full">
                      {t.blog.categoryEducation}
                    </div>
                  </div>
                  <div className="p-5 md:p-6 flex flex-col flex-1">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {t.blog.posts.windTurbineTechnology.title}
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 mb-4 line-clamp-3 flex-1">
                      {t.blog.posts.windTurbineTechnology.subtitle}
                    </p>
                    <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-xs leading-5 text-gray-500">{t.blog.posts.windTurbineTechnology.date} • {t.blog.posts.windTurbineTechnology.readTime}</span>
                      <span className="inline-flex items-center gap-1 text-primary-600 font-medium text-sm">
                        {t.blog.readMore}
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            {/* Blog Card 4 - World's Biggest Turbine */}
            <div className="group">
              <Link to="/blog/worlds-biggest-turbine" className="block h-full">
                <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 bg-white border border-gray-100 h-full flex flex-col">
                  <div className="relative h-48 md:h-56 overflow-hidden">
                    <img
                      src="/world_biggest_turbine_1.jpg"
                      alt={t.blog.posts.worldsBiggestTurbine.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute top-4 right-4 px-3 py-1 bg-primary-500 text-white text-xs font-semibold rounded-full">
                      {t.blog.categoryTechnology}
                    </div>
                  </div>
                  <div className="p-5 md:p-6 flex flex-col flex-1">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {t.blog.posts.worldsBiggestTurbine.title}
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 mb-4 line-clamp-3 flex-1">
                      {t.blog.posts.worldsBiggestTurbine.subtitle}
                    </p>
                    <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-xs leading-5 text-gray-500">{t.blog.posts.worldsBiggestTurbine.date} • {t.blog.posts.worldsBiggestTurbine.readTime}</span>
                      <span className="inline-flex items-center gap-1 text-primary-600 font-medium text-sm">
                        {t.blog.readMore}
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            {/* Blog Card 5 - Rope Access Turbine */}
            <div className="group">
              <Link to="/blog/rope-access-turbine" className="block h-full">
                <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 bg-white border border-gray-100 h-full flex flex-col">
                  <div className="relative h-48 md:h-56 overflow-hidden">
                    <img
                      src="/rope_access_turbine_1.jpg"
                      alt={t.blog.posts.ropeAccessTurbine.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute top-4 right-4 px-3 py-1 bg-secondary-600 text-white text-xs font-semibold rounded-full">
                      {t.blog.categoryInspection}
                    </div>
                  </div>
                  <div className="p-5 md:p-6 flex flex-col flex-1">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {t.blog.posts.ropeAccessTurbine.title}
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 mb-4 line-clamp-3 flex-1">
                      {t.blog.posts.ropeAccessTurbine.subtitle}
                    </p>
                    <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-xs leading-5 text-gray-500">{t.blog.posts.ropeAccessTurbine.date} • {t.blog.posts.ropeAccessTurbine.readTime}</span>
                      <span className="inline-flex items-center gap-1 text-primary-600 font-medium text-sm">
                        {t.blog.readMore}
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            {/* Blog Card 6 - Offshore Turbines */}
            <div className="group">
              <Link to="/blog/offshore-turbines" className="block h-full">
                <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 bg-white border border-gray-100 h-full flex flex-col">
                  <div className="relative h-48 md:h-56 overflow-hidden">
                    <img
                      src="/offshore_1.jpg"
                      alt={t.blog.posts.offshoreTurbines.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute top-4 right-4 px-3 py-1 bg-green-600 text-white text-xs font-semibold rounded-full">
                      {t.blog.categoryEducation}
                    </div>
                  </div>
                  <div className="p-5 md:p-6 flex flex-col flex-1">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {t.blog.posts.offshoreTurbines.title}
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 mb-4 line-clamp-3 flex-1">
                      {t.blog.posts.offshoreTurbines.subtitle}
                    </p>
                    <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-xs leading-5 text-gray-500">{t.blog.posts.offshoreTurbines.date} • {t.blog.posts.offshoreTurbines.readTime}</span>
                      <span className="inline-flex items-center gap-1 text-primary-600 font-medium text-sm">
                        {t.blog.readMore}
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-6 md:py-8 lg:py-12 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 md:mb-8 lg:mb-12">
            <h2 className="text-xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 md:mb-3 lg:mb-4">
              {t.home.technologies.title}
            </h2>
            <p className="text-sm md:text-base lg:text-xl text-gray-600 max-w-2xl mx-auto">
              {t.home.technologies.subtitle}
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-1.5 md:gap-2 lg:gap-4">
            {technologies.map((tech, index) => (
              <motion.div
                key={tech}
                className="bg-white px-2 md:px-3 lg:px-6 py-1.5 md:py-2 lg:py-3 rounded-full text-xs md:text-sm lg:text-base text-gray-700 font-medium shadow-sm border"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                {tech}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

    </div>
  )
}

export default HomeScreen 
